/* Projekte & Events im Vertrieb — Planung in drei Schritten.
 *
 *   1. Vorhaben anlegen (Event mit Termin oder Projekt mit Zieldatum)
 *   2. Bedarf: "Was brauchen wir?"  — NICHT die Bedarfsanalyse beim Kunden
 *      aus dem Verkaufsrechner, das ist ein anderes Wort für dasselbe Feld.
 *   3. Checkliste mit Deadlines, meist als Vorlauf ("14 Tage vorher")
 *
 * Der Vorlauf ist der Grund für die ganze Seite: verschiebt sich der Termin,
 * rechnet der Server alle Vorlauf-Deadlines neu — kein Nachpflegen von Hand.
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { vorhabenAPI, authAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import {
  Plus, CalendarDays, MapPin, ArrowLeft, Trash2, Download,
  AlertTriangle, CheckCircle2, ClipboardList, ListChecks
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const STATUS_LABEL = {
  geplant: 'Geplant', laufend: 'Läuft',
  abgeschlossen: 'Abgeschlossen', abgesagt: 'Abgesagt',
};

const datumDe = (iso) => (iso ? iso.split('-').reverse().join('.') : '—');

/* "noch 12 Tage" / "vor 3 Tagen" — im Alltag interessiert der Abstand,
   nicht das Datum. */
function tageText(tage) {
  if (tage === null || tage === undefined) return null;
  if (tage === 0) return 'heute';
  if (tage === 1) return 'morgen';
  if (tage > 1) return `noch ${tage} Tage`;
  return `vor ${Math.abs(tage)} Tagen`;
}

function Fortschritt({ wert }) {
  const anteil = wert.gesamt ? Math.round((wert.erledigt / wert.gesamt) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div className="h-full bg-primary rounded-full transition-all"
             style={{ width: `${anteil}%` }} />
      </div>
      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
        <span>{wert.erledigt} von {wert.gesamt} erledigt</span>
        {wert.ueberfaellig > 0 && (
          <span className="text-destructive font-medium flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />{wert.ueberfaellig} überfällig
          </span>
        )}
      </div>
    </div>
  );
}


/* ─────────────────────────────── Übersicht ─────────────────────────────── */

export function VorhabenListe() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [liste, setListe] = useState([]);
  const [meine, setMeine] = useState([]);
  const [laden, setLaden] = useState(true);
  const [zeigeNeu, setZeigeNeu] = useState(false);
  const [abgeschlossen, setAbgeschlossen] = useState(false);

  const darfVerwalten = user?.role === 'admin' || user?.role === 'manager';

  const holen = useCallback(async () => {
    try {
      const [v, m] = await Promise.all([
        vorhabenAPI.liste(abgeschlossen ? { status: 'abgeschlossen' } : undefined),
        vorhabenAPI.meineAufgaben(),
      ]);
      setListe(v.data.vorhaben);
      setMeine(m.data.aufgaben);
    } catch {
      toast.error('Konnte die Vorhaben nicht laden.');
    } finally {
      setLaden(false);
    }
  }, [abgeschlossen]);

  useEffect(() => { holen(); }, [holen]);

  return (
    <div className="space-y-5 fade-in" data-testid="vorhaben-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Projekte & Events</h1>
          <p className="text-muted-foreground text-xs sm:text-sm">
            Bedarf erfassen, Checkliste planen, Deadlines im Blick behalten
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="h-9 text-xs sm:text-sm"
                  onClick={() => setAbgeschlossen((a) => !a)} data-testid="vorhaben-filter">
            {abgeschlossen ? 'Laufende zeigen' : 'Abgeschlossene'}
          </Button>
          {darfVerwalten && (
            <Button size="sm" className="gap-1.5 h-9 text-xs sm:text-sm"
                    onClick={() => setZeigeNeu(true)} data-testid="vorhaben-neu-btn">
              <Plus className="w-3.5 h-3.5" /> Neues Vorhaben
            </Button>
          )}
        </div>
      </div>

      {/* Was bei MIR anliegt — über alle Vorhaben hinweg */}
      {meine.length > 0 && (
        <Card className="border-border/60 p-4">
          <h2 className="text-sm font-bold flex items-center gap-2 mb-3">
            <ListChecks className="w-4 h-4 text-primary" /> Meine nächsten Aufgaben
          </h2>
          <div className="space-y-1.5" data-testid="meine-aufgaben">
            {meine.slice(0, 8).map((a) => (
              <Link key={a.id} to={`/vorhaben/${a.vorhaben_id}`}
                    className="flex items-center gap-3 text-sm py-1.5 px-2 -mx-2 rounded hover:bg-muted/40 transition-colors">
                <span className={`text-xs font-medium tabular-nums w-[86px] flex-shrink-0 ${a.ueberfaellig ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {datumDe(a.faellig_am)}
                </span>
                <span className="flex-1 min-w-0 truncate">{a.titel}</span>
                <span className="text-xs text-muted-foreground hidden sm:block truncate max-w-[160px]">
                  {a.vorhaben_titel}
                </span>
              </Link>
            ))}
          </div>
        </Card>
      )}

      {laden && <p className="text-sm text-muted-foreground">Wird geladen …</p>}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {liste.map((v) => (
          <Card key={v.id} onClick={() => navigate(`/vorhaben/${v.id}`)}
                data-testid={`vorhaben-karte-${v.id}`}
                className="border-border/60 p-4 cursor-pointer hover:border-primary/40 transition-colors space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{v.titel}</p>
                <p className="text-xs text-muted-foreground capitalize">
                  {v.art === 'event' ? 'Event' : 'Projekt'}
                </p>
              </div>
              <Badge variant="secondary" className="text-[10px] flex-shrink-0">
                {STATUS_LABEL[v.status] || v.status}
              </Badge>
            </div>

            <div className="space-y-1 text-xs text-muted-foreground">
              {v.datum && (
                <p className="flex items-center gap-1.5">
                  <CalendarDays className="w-3.5 h-3.5" /> {datumDe(v.datum)}
                  {v.tage_bis !== null && (
                    <span className={v.tage_bis < 0 ? 'text-destructive' : 'font-medium text-foreground'}>
                      · {tageText(v.tage_bis)}
                    </span>
                  )}
                </p>
              )}
              {v.ort && <p className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{v.ort}</p>}
              {v.verantwortlich_name && <p>Verantwortlich: {v.verantwortlich_name}</p>}
            </div>

            <Fortschritt wert={v.fortschritt} />

            {v.bedarf_offen > 0 && (
              <p className="text-[11px] text-amber-600 dark:text-amber-500 flex items-center gap-1">
                <ClipboardList className="w-3 h-3" />
                {v.bedarf_offen} Bedarf noch nicht eingeplant
              </p>
            )}
          </Card>
        ))}
        {!laden && liste.length === 0 && (
          <p className="text-sm text-muted-foreground col-span-full py-8 text-center">
            {abgeschlossen ? 'Nichts abgeschlossen.'
              : 'Noch kein Vorhaben angelegt. Fang mit dem nächsten Event an.'}
          </p>
        )}
      </div>

      <NeuDialog open={zeigeNeu} onClose={() => setZeigeNeu(false)}
                 onFertig={(id) => navigate(`/vorhaben/${id}`)} />
    </div>
  );
}


function NeuDialog({ open, onClose, onFertig }) {
  const [form, setForm] = useState({
    titel: '', art: 'event', datum: '', ort: '', beschreibung: '',
    vorlage_uebernehmen: true,
  });
  const [busy, setBusy] = useState(false);

  const speichern = async () => {
    if (form.titel.trim().length < 2) { toast.error('Bitte einen Titel eingeben.'); return; }
    setBusy(true);
    try {
      const r = await vorhabenAPI.anlegen({ ...form, titel: form.titel.trim() });
      toast.success(r.data.bedarf_aus_vorlage
        ? `Angelegt — ${r.data.bedarf_aus_vorlage} Bedarfspunkte als Startliste`
        : 'Angelegt');
      onClose();
      onFertig(r.data.vorhaben.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Anlegen fehlgeschlagen');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Neues Vorhaben</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="vh-titel">Titel *</Label>
            <Input id="vh-titel" value={form.titel} data-testid="vh-titel"
                   placeholder="z. B. Sommerfest 2027"
                   onChange={(e) => setForm({ ...form, titel: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="vh-art">Art</Label>
              <select id="vh-art" value={form.art} data-testid="vh-art"
                      onChange={(e) => setForm({ ...form, art: e.target.value })}
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm">
                <option value="event">Event (mit Termin)</option>
                <option value="projekt">Projekt (mit Zieldatum)</option>
              </select>
            </div>
            <div>
              <Label htmlFor="vh-datum">{form.art === 'event' ? 'Termin *' : 'Zieldatum'}</Label>
              <Input id="vh-datum" type="date" value={form.datum} data-testid="vh-datum"
                     onChange={(e) => setForm({ ...form, datum: e.target.value })} />
            </div>
          </div>
          <div>
            <Label htmlFor="vh-ort">Ort</Label>
            <Input id="vh-ort" value={form.ort}
                   onChange={(e) => setForm({ ...form, ort: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="vh-text">Worum geht es?</Label>
            <Textarea id="vh-text" rows={2} value={form.beschreibung}
                      onChange={(e) => setForm({ ...form, beschreibung: e.target.value })} />
          </div>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={form.vorlage_uebernehmen} className="mt-1"
                   data-testid="vh-vorlage"
                   onChange={(e) => setForm({ ...form, vorlage_uebernehmen: e.target.checked })} />
            <span>Startliste an Bedarf übernehmen <span className="text-muted-foreground">
              (Fläche, Genehmigung, Budget, Werbemittel, Team, Material, Technik,
              Erfassung — alles änderbar)</span></span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={speichern} disabled={busy} data-testid="vh-speichern">
            {busy ? 'Speichere …' : 'Anlegen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ──────────────────────────────── Detail ───────────────────────────────── */

export function VorhabenDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [daten, setDaten] = useState(null);
  const [team, setTeam] = useState([]);
  const [fehler, setFehler] = useState('');

  const darfVerwalten = user?.role === 'admin' || user?.role === 'manager';

  const holen = useCallback(async () => {
    try {
      const r = await vorhabenAPI.detail(id);
      setDaten(r.data);
      setFehler('');
    } catch (e) {
      setFehler(e?.response?.status === 404 ? 'Dieses Vorhaben gibt es nicht (mehr).'
                                            : 'Laden fehlgeschlagen.');
    }
  }, [id]);

  useEffect(() => { holen(); }, [holen]);
  useEffect(() => {
    if (!darfVerwalten) return;
    authAPI.users().then((r) => setTeam(r.data || [])).catch(() => {});
  }, [darfVerwalten]);

  if (fehler) return (
    <div className="space-y-4">
      <p className="text-sm text-destructive">{fehler}</p>
      <Button variant="outline" onClick={() => navigate('/vorhaben')}>Zur Übersicht</Button>
    </div>
  );
  if (!daten) return <p className="text-sm text-muted-foreground">Wird geladen …</p>;

  const { vorhaben: v, bedarf, aufgaben, kategorien } = daten;
  const offenerBedarf = bedarf.filter((b) => b.status !== 'eingeplant');
  const eingeplant = bedarf.filter((b) => b.status === 'eingeplant');

  const datumAendern = async (neu) => {
    try {
      const r = await vorhabenAPI.aendern(id, { datum: neu });
      toast.success(r.data.deadlines_verschoben
        ? `Termin verschoben — ${r.data.deadlines_verschoben} Deadline(s) sind mitgewandert`
        : 'Termin geändert');
      holen();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Änderung fehlgeschlagen'); }
  };

  const statusAendern = async (status) => {
    try {
      const r = await vorhabenAPI.aendern(id, { status });
      if (status === 'abgeschlossen' && r.data.offene_aufgaben > 0) {
        toast.warning(`Abgeschlossen — ${r.data.offene_aufgaben} Punkt(e) waren noch offen`);
      } else {
        toast.success('Status geändert');
      }
      holen();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Änderung fehlgeschlagen'); }
  };

  const loeschen = async () => {
    if (!window.confirm(`„${v.titel}" mit Bedarf und Checkliste löschen? Für „ist vorbei" nimm lieber den Status „Abgeschlossen".`)) return;
    try {
      await vorhabenAPI.loeschen(id);
      toast.success('Gelöscht');
      navigate('/vorhaben');
    } catch { toast.error('Löschen fehlgeschlagen'); }
  };

  return (
    <div className="space-y-5 fade-in" data-testid="vorhaben-detail">
      <Button variant="ghost" size="sm" onClick={() => navigate('/vorhaben')}>
        <ArrowLeft className="w-4 h-4 mr-1" /> Übersicht
      </Button>

      {/* Schritt 1: Eckdaten */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">{v.titel}</h1>
          <p className="text-sm text-muted-foreground">
            {v.art === 'event' ? 'Event' : 'Projekt'}
            {v.ort && ` · ${v.ort}`}
            {v.verantwortlich_name && ` · ${v.verantwortlich_name}`}
          </p>
          {v.beschreibung && <p className="text-sm mt-2 max-w-2xl">{v.beschreibung}</p>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input type="date" value={v.datum || ''} className="h-9 w-[150px]"
                 disabled={!darfVerwalten} data-testid="vh-datum-feld"
                 onChange={(e) => e.target.value && datumAendern(e.target.value)} />
          <select value={v.status} onChange={(e) => statusAendern(e.target.value)}
                  disabled={!darfVerwalten} data-testid="vh-status"
                  className="flex h-9 rounded-md border border-input bg-background px-3 text-sm">
            {Object.entries(STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <Button variant="outline" size="sm" className="h-9 gap-1.5"
                  onClick={() => vorhabenAPI.checklisteCSV(id).then((r) => {
                    const url = window.URL.createObjectURL(new Blob([r.data]));
                    const a = document.createElement('a');
                    a.href = url; a.download = 'checkliste.csv'; a.click();
                    window.URL.revokeObjectURL(url);
                  }).catch(() => toast.error('Export fehlgeschlagen'))}>
            <Download className="w-3.5 h-3.5" /> Checkliste
          </Button>
          {darfVerwalten && (
            <Button variant="outline" size="sm" className="h-9 text-destructive"
                    onClick={loeschen} data-testid="vh-loeschen"><Trash2 className="w-3.5 h-3.5" /></Button>
          )}
        </div>
      </div>

      <Card className="border-border/60 p-4 space-y-2">
        {v.datum && (
          <p className="text-sm">
            <b>{datumDe(v.datum)}</b>
            <span className={`ml-2 ${v.tage_bis < 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
              {tageText(v.tage_bis)}
            </span>
          </p>
        )}
        <Fortschritt wert={v.fortschritt} />
        {darfVerwalten && v.datum && (
          <p className="text-[11px] text-muted-foreground">
            Termin ändern verschiebt alle Punkte mit Vorlauf automatisch mit.
            Punkte mit festem Datum bleiben stehen.
          </p>
        )}
      </Card>

      <BedarfBlock vorhabenId={id} bedarf={offenerBedarf} eingeplant={eingeplant}
                   kategorien={kategorien} team={team} hatDatum={!!v.datum}
                   onAenderung={holen} />

      <ChecklisteBlock vorhabenId={id} aufgaben={aufgaben} team={team}
                       hatDatum={!!v.datum} onAenderung={holen} />
    </div>
  );
}


/* Schritt 2: Bedarf — was brauchen wir? */
function BedarfBlock({ vorhabenId, bedarf, eingeplant, kategorien, team, hatDatum, onAenderung }) {
  const [neu, setNeu] = useState({ kategorie: 'material', was: '', menge: '' });
  const [einplanen, setEinplanen] = useState(null);   // Bedarfszeile im Planen-Modus
  const [busy, setBusy] = useState(false);
  const label = Object.fromEntries(kategorien.map((k) => [k.key, k.label]));

  const melden = async () => {
    if (neu.was.trim().length < 2) { toast.error('Was fehlt denn?'); return; }
    setBusy(true);
    try {
      await vorhabenAPI.bedarfAnlegen(vorhabenId, { ...neu, was: neu.was.trim() });
      setNeu({ kategorie: neu.kategorie, was: '', menge: '' });
      onAenderung();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Konnte den Bedarf nicht speichern');
    } finally { setBusy(false); }
  };

  return (
    <Card className="border-border/60 p-4 space-y-3" data-testid="bedarf-block">
      <div>
        <h2 className="text-sm font-bold flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-primary" /> Bedarf: Was brauchen wir?
        </h2>
        <p className="text-xs text-muted-foreground">
          Erst sammeln, dann einplanen. Jeder im Team darf hier eintragen, was fehlt.
        </p>
      </div>

      {bedarf.map((b) => (
        <div key={b.id} className="border rounded-md" data-testid={`bedarf-${b.id}`}>
          <div className="flex items-center gap-3 px-3 py-2">
            <Badge variant="outline" className="text-[10px] flex-shrink-0">
              {label[b.kategorie] || b.kategorie}
            </Badge>
            <div className="min-w-0 flex-1">
              <p className="text-sm truncate">{b.was}</p>
              {(b.menge || b.notiz) && (
                <p className="text-xs text-muted-foreground truncate">
                  {[b.menge, b.notiz].filter(Boolean).join(' · ')}
                </p>
              )}
            </div>
            <Button size="sm" variant="outline" className="h-8 text-xs flex-shrink-0"
                    data-testid={`einplanen-${b.id}`}
                    onClick={() => setEinplanen(einplanen === b.id ? null : b.id)}>
              Einplanen
            </Button>
          </div>
          {einplanen === b.id && (
            <EinplanenForm bedarf={b} vorhabenId={vorhabenId} team={team} hatDatum={hatDatum}
                           onFertig={() => { setEinplanen(null); onAenderung(); }} />
          )}
        </div>
      ))}

      {bedarf.length === 0 && (
        <p className="text-xs text-muted-foreground">
          {eingeplant.length > 0 ? 'Alles eingeplant — steht unten auf der Checkliste.'
                                 : 'Noch nichts erfasst.'}
        </p>
      )}
      {eingeplant.length > 0 && (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
          {eingeplant.length} Position(en) sind bereits eingeplant
        </p>
      )}

      {/* Neuer Bedarf */}
      <div className="flex flex-wrap gap-2 pt-2 border-t">
        <select value={neu.kategorie} data-testid="bedarf-kategorie"
                onChange={(e) => setNeu({ ...neu, kategorie: e.target.value })}
                className="flex h-9 w-[150px] rounded-md border border-input bg-background px-2 text-sm">
          {kategorien.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
        </select>
        <Input placeholder="Was brauchen wir?" value={neu.was} data-testid="bedarf-was"
               className="h-9 flex-1 min-w-[180px]"
               onKeyDown={(e) => e.key === 'Enter' && melden()}
               onChange={(e) => setNeu({ ...neu, was: e.target.value })} />
        <Input placeholder="Menge" value={neu.menge} className="h-9 w-[110px]"
               onChange={(e) => setNeu({ ...neu, menge: e.target.value })} />
        <Button size="sm" className="h-9" onClick={melden} disabled={busy}
                data-testid="bedarf-hinzu">
          <Plus className="w-3.5 h-3.5" />
        </Button>
      </div>
    </Card>
  );
}


/* Bedarf → Checklistenpunkt. Vorlauf ist vorbelegt, wenn die Startliste
   einen Vorschlag mitgebracht hat. */
function EinplanenForm({ bedarf, vorhabenId, team, hatDatum, onFertig }) {
  const [modus, setModus] = useState(hatDatum ? 'vorlauf' : 'datum');
  const [vorlauf, setVorlauf] = useState(bedarf.vorschlag_vorlauf_tage ?? 7);
  const [datum, setDatum] = useState('');
  const [wer, setWer] = useState('');
  const [busy, setBusy] = useState(false);

  const speichern = async () => {
    setBusy(true);
    try {
      await vorhabenAPI.bedarfEinplanen(vorhabenId, bedarf.id, {
        verantwortlich: wer || null,
        ...(modus === 'vorlauf' ? { vorlauf_tage: Number(vorlauf) }
                                : { faellig_am: datum }),
      });
      toast.success('Steht auf der Checkliste');
      onFertig();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Einplanen fehlgeschlagen');
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-wrap items-end gap-2 px-3 pb-3 pt-1 border-t bg-muted/20">
      {hatDatum && (
        <select value={modus} onChange={(e) => setModus(e.target.value)}
                className="flex h-9 w-[135px] rounded-md border border-input bg-background px-2 text-sm">
          <option value="vorlauf">Tage vorher</option>
          <option value="datum">festes Datum</option>
        </select>
      )}
      {modus === 'vorlauf' ? (
        <div className="flex items-center gap-1.5">
          <Input type="number" min={0} value={vorlauf} className="h-9 w-[72px]"
                 data-testid="einplanen-vorlauf"
                 onChange={(e) => setVorlauf(e.target.value)} />
          <span className="text-xs text-muted-foreground">Tage vor dem Termin</span>
        </div>
      ) : (
        <Input type="date" value={datum} className="h-9 w-[150px]"
               data-testid="einplanen-datum"
               onChange={(e) => setDatum(e.target.value)} />
      )}
      {team.length > 0 && (
        <select value={wer} onChange={(e) => setWer(e.target.value)}
                className="flex h-9 w-[150px] rounded-md border border-input bg-background px-2 text-sm">
          <option value="">Verantwortlich …</option>
          {team.map((t) => <option key={t.user_id} value={t.user_id}>{t.name}</option>)}
        </select>
      )}
      <Button size="sm" className="h-9" onClick={speichern} disabled={busy}
              data-testid="einplanen-speichern">
        {busy ? '…' : 'Auf die Checkliste'}
      </Button>
    </div>
  );
}


/* Schritt 3: Checkliste mit Deadlines */
function ChecklisteBlock({ vorhabenId, aufgaben, team, hatDatum, onAenderung }) {
  const [neu, setNeu] = useState({ titel: '', vorlauf: '', datum: '' });
  const [busy, setBusy] = useState(false);

  const abhaken = async (a) => {
    try {
      await vorhabenAPI.aufgabeAendern(vorhabenId, a.id, {
        status: a.status === 'erledigt' ? 'offen' : 'erledigt',
      });
      onAenderung();
    } catch { toast.error('Konnte nicht gespeichert werden'); }
  };

  const anlegen = async () => {
    if (neu.titel.trim().length < 2) { toast.error('Bitte einen Titel eingeben.'); return; }
    if (!neu.vorlauf && !neu.datum) {
      toast.error('Jeder Punkt braucht eine Deadline — Tage vorher oder festes Datum.');
      return;
    }
    setBusy(true);
    try {
      await vorhabenAPI.aufgabeAnlegen(vorhabenId, {
        titel: neu.titel.trim(),
        ...(neu.vorlauf ? { vorlauf_tage: Number(neu.vorlauf) } : { faellig_am: neu.datum }),
      });
      setNeu({ titel: '', vorlauf: '', datum: '' });
      onAenderung();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Anlegen fehlgeschlagen');
    } finally { setBusy(false); }
  };

  const zuweisen = async (a, userId) => {
    try {
      await vorhabenAPI.aufgabeAendern(vorhabenId, a.id, { verantwortlich: userId || null });
      onAenderung();
    } catch { toast.error('Zuweisen fehlgeschlagen'); }
  };

  return (
    <Card className="border-border/60 p-4 space-y-3" data-testid="checkliste-block">
      <div>
        <h2 className="text-sm font-bold flex items-center gap-2">
          <ListChecks className="w-4 h-4 text-primary" /> Checkliste
        </h2>
        <p className="text-xs text-muted-foreground">
          Nach Deadline sortiert. Punkte mit Vorlauf wandern mit, wenn sich der Termin verschiebt.
        </p>
      </div>

      {aufgaben.map((a) => (
        <div key={a.id} className="flex items-start gap-3 py-2 border-b last:border-0"
             data-testid={`aufgabe-${a.id}`}>
          <input type="checkbox" checked={a.status === 'erledigt'} className="mt-1"
                 data-testid={`abhaken-${a.id}`} onChange={() => abhaken(a)} />
          <div className="min-w-0 flex-1">
            <p className={`text-sm ${a.status === 'erledigt' ? 'line-through text-muted-foreground' : ''}`}>
              {a.titel}
            </p>
            <div className="flex items-center gap-2 text-xs flex-wrap">
              <span className={a.ueberfaellig ? 'text-destructive font-medium' : 'text-muted-foreground'}>
                {datumDe(a.faellig_am)}
                {a.vorlauf_tage !== null && a.vorlauf_tage !== undefined
                  && ` (${a.vorlauf_tage} Tage vorher)`}
              </span>
              {a.ueberfaellig && <Badge variant="outline" className="text-[10px] text-destructive border-destructive/40">überfällig</Badge>}
              {a.notiz && <span className="text-muted-foreground truncate max-w-[220px]">{a.notiz}</span>}
            </div>
          </div>
          {team.length > 0 ? (
            <select value={a.verantwortlich || ''} onChange={(e) => zuweisen(a, e.target.value)}
                    className="flex h-8 w-[130px] rounded-md border border-input bg-background px-2 text-xs flex-shrink-0">
              <option value="">— offen —</option>
              {team.map((t) => <option key={t.user_id} value={t.user_id}>{t.name}</option>)}
            </select>
          ) : (
            <span className="text-xs text-muted-foreground flex-shrink-0">{a.verantwortlich_name || '—'}</span>
          )}
        </div>
      ))}
      {aufgaben.length === 0 && (
        <p className="text-xs text-muted-foreground">
          Noch nichts geplant — plane den Bedarf oben ein oder trag direkt einen Punkt ein.
        </p>
      )}

      <div className="flex flex-wrap gap-2 pt-2 border-t">
        <Input placeholder="Was ist zu tun?" value={neu.titel} data-testid="aufgabe-titel"
               className="h-9 flex-1 min-w-[180px]"
               onChange={(e) => setNeu({ ...neu, titel: e.target.value })} />
        {hatDatum && (
          <Input type="number" min={0} placeholder="Tage vorher" value={neu.vorlauf}
                 className="h-9 w-[120px]" data-testid="aufgabe-vorlauf"
                 onChange={(e) => setNeu({ ...neu, vorlauf: e.target.value, datum: '' })} />
        )}
        <Input type="date" value={neu.datum} className="h-9 w-[150px]" data-testid="aufgabe-datum"
               onChange={(e) => setNeu({ ...neu, datum: e.target.value, vorlauf: '' })} />
        <Button size="sm" className="h-9" onClick={anlegen} disabled={busy}
                data-testid="aufgabe-hinzu">
          <Plus className="w-3.5 h-3.5" />
        </Button>
      </div>
    </Card>
  );
}

export default VorhabenListe;
